import React, { useEffect, useRef, useState } from "react";
import { Box, Drawer, IconButton, Typography, CircularProgress, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@Compat/makeStyles";
import { siteTokens } from "../siteTokens";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import { useDemoDrawer } from "../DemoDrawerContext";
import { track } from "../../../core/analytics/ga4";
import { useSiteContent } from "../SiteContentContext";
// 360 px WebP: the logo is shown at most 180 px wide here (the brochure
// keeps the full-size PNG for print).
import Logo from "../assets/logo_header.webp";
import LogoWhite from "../assets/logo_header_white.webp";
import { useColorMode } from "../ColorModeContext";
import FounderAvatar from "./FounderAvatar";
import { SHOW_FOUNDER } from "../founder";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_CSS_SRC = "https://assets.calendly.com/assets/external/widget.css";

/** Calendly's embed messages → GA4 events. generate_lead is the key event (a
 *  booked demo); the two before it locate the drop-off inside Calendly. */
const CALENDLY_STEPS: Record<string, string> = {
  "calendly.event_type_viewed": "demo_calendar_view",
  "calendly.date_and_time_selected": "demo_time_selected",
  "calendly.event_scheduled": "generate_lead",
};

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  paper: {
    width: 720,
    maxWidth: "100vw",
    height: "100vh",
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // Hide the native scrollbar on every layer that might expose one
    // (the paper itself, the body, and the Calendly host). Scroll
    // still works — wheel, touch, and the iframe's own internal
    // scroll are unaffected; only the visual chrome on the right edge
    // is removed.
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0
    },
    "& *": {
      scrollbarWidth: "none",
      msOverflowStyle: "none"
    },
    "& *::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0
    },
    [theme.breakpoints.down("sm")]: {
      width: "100vw"
    }
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    zIndex: 2
  },
  title: {
    fontWeight: 700,
    fontSize: "1.05rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  // Who the visitor will meet — a person, not just a calendar.
  host: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.75, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: tk.accentA(tk.dark ? 0.1 : 0.04)
  },
  hostTitle: {
    fontWeight: 700,
    fontSize: "0.98rem",
    lineHeight: 1.3,
    color: theme.palette.text.primary
  },
  hostSubtitle: {
    fontSize: "0.82rem",
    lineHeight: 1.4,
    color: theme.palette.text.secondary
  },
  body: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    background: theme.palette.background.paper
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    background: theme.palette.background.paper,
    pointerEvents: "none",
    zIndex: 1
  },
  // Calendly mounts an iframe inside this div via their script. Their
  // inline widget needs a min-height — without it the iframe collapses
  // to 0 tall and the loader sticks forever.
  calendlyHost: {
    width: "100%",
    height: "100%",
    minHeight: 630,
    minWidth: 320
  },
  footer: {
    padding: theme.spacing(1.25, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    background: theme.palette.background.paper
  },
  footerHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem"
  },
  fallbackLink: {
    textTransform: "none",
    fontWeight: 600
  }
  };
});

// Load the Calendly script + stylesheet exactly once across the app.
// Calendly's inline widget requires the script to be present in the
// document; plain <iframe src="https://calendly.com/..."> is blocked
// by their X-Frame-Options.
const ensureCalendlyAssets = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.resolve();
  const w = window as typeof window & { Calendly?: unknown };
  if (w.Calendly) return Promise.resolve();

  return new Promise((resolve) => {
    if (!document.querySelector(`link[href="${CALENDLY_CSS_SRC}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_CSS_SRC;
      document.head.appendChild(link);
    }
    let script = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (script) {
      // Already loading from a previous open — wait for it.
      if ((script as HTMLScriptElement & { _loaded?: boolean })._loaded) {
        resolve();
      } else {
        script.addEventListener("load", () => resolve(), { once: true });
      }
      return;
    }
    script = document.createElement("script");
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => {
      (script as HTMLScriptElement & { _loaded?: boolean })._loaded = true;
      resolve();
    }, { once: true });
    document.body.appendChild(script);
  });
};

const DemoDrawer: React.FC = () => {
  const classes = useStyles();
  const { isOpen, url, close } = useDemoDrawer();
  const { content } = useSiteContent();
  const { mode } = useColorMode();
  // White-text logo only on dark theme; dark-text variant on light.
  const logoSrc = mode === "dark" ? LogoWhite : Logo;
  const drawerStrings = (content as { demoDrawer?: {
    title?: string;
    closeAriaLabel?: string;
    loading?: string;
    footerHint?: string;
    openInNewTab?: string;
    hostTitle?: string;
    hostSubtitle?: string;
  } }).demoDrawer ?? {};
  // English fallbacks in case a locale is missing the demoDrawer block
  // (defensive — every shipped locale has it, but a partial migration
  // shouldn't crash the drawer).
  const titleLabel = drawerStrings.title ?? "Book a 30-min demo";
  const closeAriaLabel = drawerStrings.closeAriaLabel ?? "Close demo drawer";
  const loadingLabel = drawerStrings.loading ?? "Loading the calendar…";
  const footerHintLabel = drawerStrings.footerHint ?? "Powered by Calendly · We won't share your data.";
  const openInNewTabLabel = drawerStrings.openInNewTab ?? "Open in new tab";
  const hostTitleLabel = drawerStrings.hostTitle ?? "Your demo is with Vasco, the founder";
  const hostSubtitleLabel = drawerStrings.hostSubtitle ?? "30 minutes, on your own business, no sales script.";
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);

  // Inject a global stylesheet that hides scrollbars on the MUI
  // Drawer paper and every descendant — JSS pseudo-element selectors
  // emitted by `makeStyles` weren't reaching whichever inner element
  // was rendering the visible scrollbar (likely the Calendly host or
  // an MUI Modal wrapper). A real `<style>` tag with `!important`
  // beats every cascade.
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-demo-drawer-no-scrollbar", "true");
    styleEl.textContent = `
      .MuiDrawer-root .MuiDrawer-paper,
      .MuiDrawer-root .MuiDrawer-paper * {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      .MuiDrawer-root .MuiDrawer-paper::-webkit-scrollbar,
      .MuiDrawer-root .MuiDrawer-paper *::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Mount Calendly's inline widget each time the drawer opens. Two
  // strategies are used together to hide the spinner reliably:
  //   1. postMessage listener for `calendly.*` events — Calendly's
  //      iframe posts these as soon as it renders, so the spinner
  //      goes away the moment the calendar is visible.
  //   2. A 3s safety fallback in case (1) is blocked (rare, but
  //      defensive — we don't want a stuck spinner if Calendly's
  //      message format changes).
  // Deps are only [isOpen, url] — the previous theme palette deps
  // caused the effect to re-run on every theme settling tick which
  // reset `loading` back to true mid-render.
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    let cancelled = false;
    let fallbackTimer: number | undefined;
    const sentSteps = new Set<string>();
    track("demo_open", { lead_source: "calendly_demo" });

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      const eventName = typeof data === "string"
        ? data
        : (data && typeof data === "object" && "event" in data ? (data as { event?: string }).event : undefined);
      if (typeof eventName === "string" && eventName.indexOf("calendly") === 0) {
        if (!cancelled) setLoading(false);
        if (event.origin !== "https://calendly.com") return;
        // The booking happens inside Calendly's iframe; its messages are the
        // only view of that funnel: calendar shown → time picked → booked.
        // Each step is sent once per opening, so a drop-off shows where it is.
        const step = CALENDLY_STEPS[eventName];
        if (step && !sentSteps.has(eventName)) {
          sentSteps.add(eventName);
          track(step, { lead_source: "calendly_demo" });
        }
      }
    };
    window.addEventListener("message", handleMessage);

    ensureCalendlyAssets().then(() => {
      if (cancelled) return;
      const host = hostRef.current;
      if (!host) return;
      host.innerHTML = "";
      const w = window as typeof window & {
        Calendly?: {
          initInlineWidget: (opts: { url: string; parentElement: HTMLElement; prefill?: object; utm?: object }) => void;
        };
      };
      if (w.Calendly?.initInlineWidget) {
        w.Calendly.initInlineWidget({ url, parentElement: host });
        // Safety net — if Calendly's postMessage never arrives
        // (some adblockers strip them) clear the spinner anyway.
        fallbackTimer = window.setTimeout(() => {
          if (!cancelled) setLoading(false);
        }, 3000);
      } else {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      window.removeEventListener("message", handleMessage);
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
    };
  }, [isOpen, url]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      ModalProps={{ keepMounted: false }}
      PaperProps={{ className: classes.paper }}
    >
      <div className={classes.header}>
        <img
          src={logoSrc}
          alt={titleLabel}
          style={{ height: 44, width: "auto", display: "block" }}
        />
        <IconButton size="small" onClick={close} aria-label={closeAriaLabel}>
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 20 }} />
        </IconButton>
      </div>
      {SHOW_FOUNDER ? (
      <div className={classes.host}>
        <FounderAvatar size={48} />
        <div>
          <Typography component="p" className={classes.hostTitle}>{hostTitleLabel}</Typography>
          <Typography component="p" className={classes.hostSubtitle}>{hostSubtitleLabel}</Typography>
        </div>
      </div>
      ) : null}
      <Box className={classes.body}>
        {loading ? (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="textSecondary">
              {loadingLabel}
            </Typography>
          </div>
        ) : null}
        <div ref={hostRef} className={classes.calendlyHost} />
      </Box>
      <div className={classes.footer}>
        <Typography component="span" className={classes.footerHint}>
          {footerHintLabel}
        </Typography>
        <Button
          size="small"
          variant="text"
          color="primary"
          className={classes.fallbackLink}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 20 }} />}
        >
          {openInNewTabLabel}
        </Button>
      </div>
    </Drawer>
  );
};

export default DemoDrawer;
